import React, { useState } from 'react';
import { TEMPLATE_PRESETS, TemplatePreset } from '@/constants/templates';
import { QRRenderer } from '@/components/qr/QRRenderer';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { LayoutTemplate, Search } from 'lucide-react';

interface TemplatesGalleryProps {
  currentRawText: string;
  onApplyTemplate: (template: TemplatePreset) => void;
}

export const TemplatesGallery: React.FC<TemplatesGalleryProps> = ({
  currentRawText,
  onApplyTemplate
}) => {
  const { showToast } = useToast();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['all', 'Modern', 'Gradient', 'Business', 'Creative', 'Dark'];

  const filtered = TEMPLATE_PRESETS.filter((tpl) => {
    const matchesCat = activeCategory === 'all' || tpl.category === activeCategory;
    const matchesSearch =
      tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tpl.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleSelect = (tpl: TemplatePreset) => {
    onApplyTemplate(tpl);
    showToast('success', `Applied "${tpl.name}" Style`, 'Design styling updated.');
  };

  return (
    <div className="w-full space-y-6">
      {/* Header Store Utility Card */}
      <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <div>
          <h3 className="text-[21px] font-semibold text-foreground tracking-tight flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-primary" />
            Designer QR Templates Gallery
          </h3>
          <p className="text-[14px] text-muted-foreground mt-0.5">
            Professionally crafted aesthetic styles, callout badges, and finder treatments
          </p>
        </div>

        {/* Filter Pills and Search */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex items-center gap-1.5 p-1 bg-muted rounded-full overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-normal tracking-tight transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-card text-primary font-medium shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat === 'all' ? 'All Styles' : cat}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-3.5 py-1.5 text-[14px] rounded-full bg-muted border border-border text-foreground focus:outline-none focus:border-ring tracking-tight"
            />
          </div>
        </div>
      </div>

      {/* Grid of Template Showcase Cards (Museum Gallery style) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((tpl) => (
          <div
            key={tpl.id}
            onClick={() => handleSelect(tpl)}
            className="group p-6 rounded-2xl bg-card border border-border hover:border-muted-foreground transition-all duration-150 cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-normal px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {tpl.category}
                </span>
                <span className="text-[14px] font-semibold text-foreground tracking-tight">
                  {tpl.name}
                </span>
              </div>

              {/* Centered QR Preview with signature Product Shadow */}
              <div className="p-4 rounded-xl bg-muted border border-border flex items-center justify-center mb-4">
                <div className="p-2.5 rounded-lg bg-white product-shadow">
                  <QRRenderer
                    data={currentRawText || 'https://qr.planmaster.in'}
                    design={tpl.design}
                    size={160}
                  />
                </div>
              </div>

              <p className="text-[14px] text-muted-foreground mb-4 leading-relaxed line-clamp-2">
                {tpl.description}
              </p>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSelect(tpl)}
              className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors"
            >
              Apply Style
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
